import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";

const SearchIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round">
		<circle cx="11" cy="11" r="8" />
		<path d="m21 21-4.3-4.3" />
	</svg>
);

const Container = styled.div`
	position: relative;
	width: 100%;
	max-width: 320px;
    ${props => props.disabled && css `
		pointer-events: none;
		opacity: 0.5;
	`}
`;

const InputWrapper = styled.div`
	position: relative;
`;

const Input = styled.input`
	width: 100%;
	padding: 8px;
	padding-right: 32px;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	outline: none;
	transition: all 0.2s;

	&:focus {
		border-color: #3b82f6;
		box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
	}
`;

const IconWrapper = styled.div`
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	width: 20px;
	height: 20px;
	color: #9ca3af;
`;

const Dropdown = styled.div`
	position: absolute;
	z-index: 10;
	width: 100%;
	margin-top: 4px;
	background: white;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
	max-height: 240px;
	overflow-y: auto;
`;

const DropdownItem = styled.div`
	padding: 8px 16px;
	cursor: pointer;
	transition: background-color 0.2s;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: "#EFF6FF";

	&:hover {
		background-color: #f3f4f6;
	}

	span.value {
		color: #6b7280;
		font-size: 0.9em;
	}

    ${props => props.isSelected && css`
        background-color: #dbeafe;
        color: #2563eb;
    `}
`;

const SelectedBadge = styled.span`
	color: #2563eb;
	font-size: 12px;
	padding: 2px 6px;
	border-radius: 4px;
	background-color: #dbeafe;
`;

const NoResults = styled.div`
	padding: 8px 16px;
	color: #6b7280;
`;

const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

const SearchableDropdown = ({ list = [], onChange = () => {}, defaultSelected = null, disabled = false }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(defaultSelected ? defaultSelected.head : "");
	const [selectedItem, setSelectedItem] = useState(defaultSelected);
	const dropdownRef = useRef(null);

	const debouncedSearchTerm = useDebounce(searchTerm, 200);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				// Reset search term to selected item if available, or empty if not
				setSearchTerm(selectedItem ? selectedItem.head : "");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [selectedItem]);

	const filteredList = list?.length ? list.filter((item) => {
        if(!debouncedSearchTerm) return true;
        return item.head.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    }) : [];

	const handleSelect = (item) => {
		setSelectedItem(item);
		setSearchTerm(item.head);
		setIsOpen(false);
		onChange(item);
	};

	const handleInputChange = (e) => {
		setSearchTerm(e.target.value);
		setIsOpen(true);
	};

	return (
		<Container ref={dropdownRef} disabled={disabled} >
			<InputWrapper>
				<Input
					type="text"
					value={searchTerm}
					onChange={handleInputChange}
					onClick={() => setIsOpen(true)}
					placeholder="Search..."
				/>
				<IconWrapper>
					<SearchIcon />
				</IconWrapper>
			</InputWrapper>

			{isOpen && (
				<Dropdown>
					{filteredList.length > 0 ? (
						filteredList.map((item, index) => (
							<DropdownItem key={index} onClick={() => handleSelect(item)} isSelected={selectedItem?.value === item.value}>
								<div>
									{item.head}
								</div>
							</DropdownItem>
						))
					) : (
						<NoResults>No results found</NoResults>
					)}
				</Dropdown>
			)}
		</Container>
	);
};

export default SearchableDropdown;
